import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Text } from 'rebass/styled-components';
import styled from 'styled-components';
import theme from 'styled-theming';
import {
  FiSend, FiUserCheck, FiBriefcase,
} from 'react-icons/fi';
import EditorField from '../Editor/editor-field';
import ElidableText from '../elidable-text';

// It sbould render an action type
// onChange should set the type value in the actions array, specified by the rowIndex

const textColor = theme('mode', {
  light: (props) => props.theme.colors.deepGrey,
  dark: (props) => props.theme.colors.white,
});

const ActionLabelWrapper = styled(Flex)`
  color: ${textColor};
`;

const SendMessageIcon = styled(FiSend)`
  flex-shrink: 0;
  stroke-width: 1.25;
`;

const TransferToAgentIcon = styled(FiUserCheck)`
  flex-shrink: 0;
  stroke-width: 1.25;
`;

const TransferToSkillIcon = styled(FiBriefcase)`
  flex-shrink: 0;
  stroke-width: 1.25;
`;

const getActionIcon = (type) => {
  switch (type) {
    case 'TRANSFER_TO_SKILL':
      return <TransferToSkillIcon size={18} />;
    case 'TRANSFER_TO_AGENT':
      return <TransferToAgentIcon size={18} />;
    default:
      return <SendMessageIcon size={18} />;
  }
};

const getActionName = (type) => {
  switch (type) {
    case 'TRANSFER_TO_SKILL':
      return 'Transfer to skill';
    case 'TRANSFER_TO_AGENT':
      return 'Transfer to agent';
    default:
      return 'Send message';
  }
};

const getValueTypes = (type) => ['TRANSFER_TO_SKILL', 'TRANSFER_TO_AGENT', 'SEND_MESSAGE'];

const parseValue = (value) => {
  if (value === 'TRANSFER_TO_AGENT') {
    return 'Transfer to agent';
  }
  if (value === 'TRANSFER_TO_SKILL') {
    return 'Transfer to skill';
  }
  if (value === 'SEND_MESSAGE') {
    return 'Send message';
  }
  return 'undefined';
};

function PolicyActionType(props) {
  const {
    isEditing, rowItem, rowIndex, temporaryPolicy, validateObject,
  } = props;

  const selectItem = (selector, name, index) => {
    // Update the temporary object with the new information
    const newPolicy = { ...temporaryPolicy };
    newPolicy.actions[index][selector] = name;
    validateObject(newPolicy);
  };

  if (isEditing) {
    return (
      <EditorField
        isEditing
        value={parseValue(rowItem.type)}
        placeholder="Choose a type"
        selectItem={(name) => {
          if (rowItem.type !== name) {
            // Set type to be the name
            selectItem('type', name, rowIndex);
            // Reset action payload
            switch (name) {
              case 'SEND_MESSAGE':
                selectItem('payload', { message: '' }, rowIndex);
                break;
              case 'TRANSFER_TO_AGENT':
                selectItem('payload', { userId: '', skillId: '-1' }, rowIndex);
                break;
              case 'TRANSFER_TO_SKILL':
                selectItem('payload', { skillId: '' }, rowIndex);
                break;
              default:
                break;
            }
          }
        }}
        items={getValueTypes(rowItem.type)}
        noFreeText
        hasTransfer={temporaryPolicy.actions.length >= 2 && temporaryPolicy.actions.reduce(
          (accumulator, action) => accumulator || action.type.startsWith('TRANSFER_TO'), false,
        )}
      />
    );
  }

  return (
    <ActionLabelWrapper mx={2} alignItems="center" data-testid="policy-action-type">
      {getActionIcon(rowItem.type)}
      <Text variant="primary" ml={2} data-testid="action-row-label">
        <ElidableText text={getActionName(rowItem.type)} lineCount={1} />
      </Text>
    </ActionLabelWrapper>
  );
}

PolicyActionType.propTypes = {
  rowItem: PropTypes.object.isRequired,
  isEditing: PropTypes.bool.isRequired,
  rowIndex: PropTypes.number.isRequired,
  temporaryPolicy: PropTypes.object.isRequired,
  validateObject: PropTypes.func.isRequired,
};

export default PolicyActionType;
